/* eslint-disable max-lines */
import { bbox } from '@turf/bbox';
import { buffer } from '@turf/buffer';
import { point } from '@turf/helpers';
import { GetStaticPathsResult, GetStaticPropsContext } from 'next';
import invariant from 'tiny-invariant';

import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { format, sub } from 'date-fns';
import { getBlogPostsByPlace } from '../../../../src/api/contentful/blog/getBlogPostsByPlace';
import { getUSPlaceBySlug } from '../../../../src/api/contentful/blog/getUSPlaceBySlug';
import { getRutilusGraphqlClient } from '../../../../src/api/graphql/getRutilusGraphqlClient';
import { queryRutilusStatic } from '../../../../src/api/graphql/queryRutilusStatic';
import { AppLogic } from '../../../../src/components/AppLogic/AppLogic';
import { GlobalLayout } from '../../../../src/components/Layouts/GlobalLayout';
import { NOT_FOUND_WITH_REVALIDATION, REVALIDATE } from '../../../../src/constants/next';
import { reportMykissError } from '../../../../src/helpers/errorHandling';
import { turfBoundsToRutilusBounds } from '../../../../src/helpers/location';
import { constructPlaceURL } from '../../../../src/helpers/places/constructPlaceURL';
import { getSEOStates } from '../../../../src/helpers/states/getSEOStates';
import { IBoundingBox } from '../../../../src/interfaces/geo';
import {
  GetPlace,
  GetPlaceCoordinates,
  GetPlaceCoordinatesVariables,
  GetPlaceNearbyPlaces,
  GetPlaceNearbyPlacesVariables,
  GetPlaceTopAnglers,
  GetPlaceTopAnglersVariables,
  GetPlaceTopSpecies,
  GetPlaceTopSpeciesVariables,
  GetPlaceTopWaters,
  GetPlaceTopWatersVariables,
  GetPlaceVariables,
  GetPlaceWatersByTopSpecies,
  GetPlaceWatersByTopSpeciesVariables,
} from '../../../../src/interfaces/graphql';
import { StaticResult } from '../../../../src/interfaces/nextjs';
import {
  getPlace,
  getPlaceCoordinates,
  getPlaceNearbyPlaces,
  getPlaceTopAnglers,
  getPlaceTopSpecies,
  getPlaceTopWaters,
  getPlaceWatersByTopSpecies,
} from '../../../../src/queries/rutilus/Places';
import { NR_OF_TOP_SPECIES_TO_SHOW } from '../../../../src/views/Place/components/TopSpeciesSection/TopSpeciesSection';
import { IPlaceProps, Place } from '../../../../src/views/Place/Place';

const NUMBER_OF_TOP_WATERS = 10;
const RADIUS_OF_FISHINGSPOTS_IN_KM = 20;

type PlaceParams = {
  countryCode: string;
  regionCode: string;
  placeSlug: string;
};

interface RequestProps {
  params: PlaceParams;
  client: ApolloClient<NormalizedCacheObject>;
}

interface DataWithDependencies {
  boundingBox: IBoundingBox | null;
  coordinates?: IPlaceProps['coordinates'];
  topWaters: IPlaceProps['topWaters'];
  topSpeciesSummary: IPlaceProps['topSpeciesSummary'] | null;
  topSpeciesFishingSpots: IPlaceProps['topSpeciesFishingSpots'] | null;
}
interface DataWithoutDependencies {
  blogPosts: IPlaceProps['blogPosts'] | null;
  place?: IPlaceProps['place'];
  nearbyPlaces: IPlaceProps['nearbyPlaces'] | null;
  topAnglers: IPlaceProps['topAnglers'] | null;
  topAnglersDateString: string;
}

async function getDataWithDepedencies({
  client,
  params,
}: RequestProps): Promise<DataWithDependencies> {
  const getPlaceTopSpeciesQuery = queryRutilusStatic<
    GetPlaceTopSpecies,
    GetPlaceTopSpeciesVariables
  >(client, {
    query: getPlaceTopSpecies,
    variables: params,
  });

  const getPlaceCoordinatesQuery = queryRutilusStatic<
    GetPlaceCoordinates,
    GetPlaceCoordinatesVariables
  >(client, {
    query: getPlaceCoordinates,
    variables: params,
  });

  const [getPlaceCoordinatesResult, getPlaceTopSpeciesResult] = await Promise.all([
    getPlaceCoordinatesQuery,
    getPlaceTopSpeciesQuery,
  ]);

  const longitude = getPlaceCoordinatesResult.response?.data.entry.longitude;
  const latitude = getPlaceCoordinatesResult.response?.data.entry.latitude;

  if (!longitude || !latitude) {
    return {
      boundingBox: null,
      topWaters: null,
      topSpeciesSummary: null,
      topSpeciesFishingSpots: null,
    };
  }

  const buffered = buffer(point([longitude, latitude]), RADIUS_OF_FISHINGSPOTS_IN_KM, {
    units: 'kilometers',
  });
  const turfbbox = buffered && bbox(buffered);
  const boundingBox = turfbbox && turfBoundsToRutilusBounds(turfbbox);

  if (!boundingBox) {
    return {
      boundingBox: null,
      coordinates: { latitude, longitude },
      topWaters: null,
      topSpeciesSummary: null,
      topSpeciesFishingSpots: null,
    };
  }

  const getPlaceTopWatersResult = await queryRutilusStatic<
    GetPlaceTopWaters,
    GetPlaceTopWatersVariables
  >(client, {
    query: getPlaceTopWaters,
    variables: {
      boundingBox,
      first: NUMBER_OF_TOP_WATERS,
    },
  });
  const topWaters = getPlaceTopWatersResult.response?.data.entry ?? null;

  const topSpecies =
    getPlaceTopSpeciesResult.response?.data.entry.topSpeciesSummary.edges?.slice(
      0,
      NR_OF_TOP_SPECIES_TO_SHOW,
    ) ?? [];

  const watersByTopSpeciesQueries = topSpecies.flatMap(edge => {
    const speciesId = edge?.node?._id;
    return speciesId
      ? [
          queryRutilusStatic<GetPlaceWatersByTopSpecies, GetPlaceWatersByTopSpeciesVariables>(
            client,
            {
              query: getPlaceWatersByTopSpecies,
              variables: {
                ...params,
                boundingBox,
                speciesIDs: [speciesId],
              },
            },
          ),
        ]
      : [];
  });

  const watersByTopSpeciesResults = await Promise.all(watersByTopSpeciesQueries);

  const topSpeciesFishingSpots = topSpecies.flatMap((edge, index) => {
    const speciesId = edge?.node?._id;
    const watersByTopSpeciesResult = watersByTopSpeciesResults[index];

    if (!speciesId) {
      return [];
    }

    return {
      speciesId,
      nrOfWaters: watersByTopSpeciesResult.response?.data.entry.totalCount ?? 0,
    };
  });

  return {
    boundingBox,
    coordinates: { latitude, longitude },
    topWaters,
    topSpeciesSummary: getPlaceTopSpeciesResult.response?.data.entry?.topSpeciesSummary ?? null,
    topSpeciesFishingSpots,
  };
}

async function getDataWithoutDependencies({
  client,
  params,
}: RequestProps): Promise<DataWithoutDependencies> {
  const getPlaceQuery = queryRutilusStatic<GetPlace, GetPlaceVariables>(client, {
    query: getPlace,
    variables: params,
  });

  const getPlaceNearbyPlacesQuery = queryRutilusStatic<
    GetPlaceNearbyPlaces,
    GetPlaceNearbyPlacesVariables
  >(client, {
    query: getPlaceNearbyPlaces,
    variables: params,
  });

  const endDate = new Date();
  const startDate = sub(endDate, { months: 3 });

  const getPlaceTopAnglersQuery = queryRutilusStatic<
    GetPlaceTopAnglers,
    GetPlaceTopAnglersVariables
  >(client, {
    query: getPlaceTopAnglers,
    variables: {
      ...params,
      catchesDateRange: { startDate: startDate, endDate: endDate },
    },
  });

  const placeUrl = constructPlaceURL(params.countryCode, params.regionCode, params.placeSlug);
  const result = await getUSPlaceBySlug(placeUrl);
  const blogPostsQuery = result ? getBlogPostsByPlace(3, result.sys.id) : undefined;

  const [
    getPlaceResult,
    getPlaceNearbyPlacesResult,
    getPlaceTopAnglersResult,
    blogPostsQueryResult,
  ] = await Promise.all([
    getPlaceQuery,
    getPlaceNearbyPlacesQuery,
    getPlaceTopAnglersQuery,
    blogPostsQuery,
  ]);

  return {
    blogPosts: blogPostsQueryResult ? blogPostsQueryResult.items : null,
    place: getPlaceResult.response?.data.entry,
    nearbyPlaces: getPlaceNearbyPlacesResult.response?.data.entry.nearbyPlaces ?? null,
    topAnglers: getPlaceTopAnglersResult.response?.data.entry?.topAnglers ?? null,
    topAnglersDateString: format(startDate, 'LLLL yyyy'),
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<PlaceParams>): StaticResult<IPlaceProps> {
  invariant(params);

  const { countryCode, regionCode, placeSlug } = params;
  const client = getRutilusGraphqlClient({ serverMode: 'static' });

  const requestProps: RequestProps = { client, params };

  const containsUpperCase =
    countryCode !== countryCode.toLowerCase() ||
    placeSlug !== placeSlug.toLowerCase() ||
    regionCode !== regionCode.toLowerCase();

  if (containsUpperCase) {
    return {
      redirect: {
        destination: `/cities/${countryCode}/${regionCode}/${placeSlug}`.toLowerCase(),
        permanent: true,
      },
    };
  }

  try {
    const [
      { boundingBox, coordinates, topWaters, topSpeciesSummary, topSpeciesFishingSpots },
      { blogPosts, place, nearbyPlaces, topAnglers, topAnglersDateString },
    ] = await Promise.all([
      getDataWithDepedencies(requestProps),
      getDataWithoutDependencies(requestProps),
    ]);

    if (!place || !coordinates) {
      return NOT_FOUND_WITH_REVALIDATION;
    }

    // get SEO States
    const countryId = place.country.id;
    const states = countryId ? getSEOStates(countryId) : [];

    return {
      revalidate: REVALIDATE.HOURLY,
      props: {
        _pageId: place._id,
        _pageSubject: place.name,
        _page: 'Place',
        _pageTitle: 'Find Spots, Species & Tips',
        place,
        blogPosts,
        boundingBox,
        coordinates,
        nearbyPlaces,
        states: states,
        topWaters,
        topSpeciesSummary,
        topAnglers,
        topAnglersDateString,
        topSpeciesFishingSpots,
      },
    };
  } catch (error) {
    reportMykissError(error);
    return NOT_FOUND_WITH_REVALIDATION;
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult<PlaceParams>> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

Place.getLayout = function getLayout(page: any) {
  return (
    <AppLogic pageProps={page.props}>
      <GlobalLayout>{page}</GlobalLayout>
    </AppLogic>
  );
};

export default Place;
